class CartDrawerShopTheLook {
  constructor() {
    this.apiUrl = "https://shopify.shopthelook.app/api/v1/shop/product-look";
    this.apiToken = "695970bc893d288315903f0fd06be987f3e89b4829282152721d575cf3a646d0";
    this.storageKey = "sopCartDrawerShopTheLook";
    this.requestId = 0;
    this.variantCache = new Map();

    ["theme:open-cart-drawer", "theme:cart-add", "theme:cart-change"].forEach((eventName) => {
      document.addEventListener(eventName, () => this.refresh());
    });

    document.addEventListener("click", (event) => this.onClick(event));
  }

  async refresh() {
    const host = document.querySelector("[data-cart-drawer-shop-the-look]");
    if (!host) return;

    const requestId = ++this.requestId;
    host.hidden = true;
    host.innerHTML = "";

    try {
      const cart = await this.fetchJson(`${window.Shopify.routes.root}cart.js`);
      if (requestId !== this.requestId) return;

      if (!cart.items.length) {
        sessionStorage.removeItem(this.storageKey);
        return;
      }

      const cartProductIds = new Set(cart.items.map((item) => String(item.product_id)));
      const state = this.readState();
      state.completedProductIds = state.completedProductIds.filter((id) => cartProductIds.has(id));

      let match = null;

      for (const item of cart.items) {
        const productId = String(item.product_id);
        const candidate = await this.getIncompleteLook(productId, cartProductIds);
        if (!candidate?.missing.length) continue;

        const activeAnchorChanged = state.activeAnchorId !== productId;
        state.activeAnchorId = productId;
        state.activeProductIds = candidate.productIds;
        if (activeAnchorChanged) state.activeRecommendationIndex = 0;
        match = candidate;
        break;
      }

      if (!match) {
        state.activeAnchorId = null;
        state.activeProductIds = [];
        state.activeRecommendationIndex = 0;
      }
      if (match?.missing.length) {
        state.activeRecommendationIndex = this.clampIndex(state.activeRecommendationIndex, match.missing.length);
      }

      this.writeState(state);
      if (!match || requestId !== this.requestId) return;

      this.render(host, match.missing[state.activeRecommendationIndex], {
        index: state.activeRecommendationIndex,
        total: match.missing.length,
      });
    } catch (error) {
      console.warn("Cart drawer Shop the Look could not be loaded", error);
    }
  }

  async getIncompleteLook(productId, cartProductIds) {
    const response = await fetch(`${this.apiUrl}?mainProductId=${encodeURIComponent(productId)}`, {
      headers: { Authorization: `Bearer ${this.apiToken}` },
    });

    if (!response.ok) return null;
    const data = await response.json();
    const items = Array.isArray(data.items) ? data.items : [];
    if (!items.length) return null;

    const availableRecommendations = [];
    const missingItems = items.filter((item) => !cartProductIds.has(String(item.id)));
    for (const item of missingItems) {
      if (await this.hasAvailableVariant(item)) availableRecommendations.push(item);
    }

    return {
      productIds: items.map((item) => String(item.id)),
      missing: availableRecommendations,
    };
  }

  async hasAvailableVariant(product) {
    const variantIds = (Array.isArray(product.variants) ? product.variants : [])
      .map((variant) => this.variantId(variant.id || variant.shopifyVariantId))
      .filter((id) => id && id !== "0");

    if (!variantIds.length) return false;

    for (const variantId of variantIds) {
      try {
        const variant = await this.getVariant(variantId);
        if (variant.available !== false) return true;
      } catch (error) {
        // Keep the recommendation if Shopify availability cannot be confirmed.
        return true;
      }
    }

    return false;
  }

  render(host, product, navigation = { index: 0, total: 1 }) {
    host.classList.remove("cart-drawer-look--single-variant");
    const variants = (Array.isArray(product.variants) ? product.variants : []).filter((variant) => {
      const id = this.variantId(variant.id || variant.shopifyVariantId);
      return id && id !== "0" && !String(variant.title || "").toLowerCase().startsWith("select ");
    });

    const hasMultipleRecommendations = navigation.total > 1;
    const options = variants
      .map((variant) => {
        const id = this.variantId(variant.id || variant.shopifyVariantId);
        return `<option value="${this.escapeAttribute(id)}">${this.escape(variant.title || "Variant")}</option>`;
      })
      .join("");

    const image = this.imageUrl(product);
    const price = product.price || product.minVariantPrice || "";

    host.innerHTML = `
      <div class="cart-drawer-look__product">
        <div class="cart-drawer-look__media">
          <img src="${this.escapeAttribute(image)}" alt="${this.escapeAttribute(product.title || "Complete your look")}" loading="lazy" data-cart-drawer-look-image>
        </div>
        <div class="cart-drawer-look__details">
          <div class="cart-drawer-look__header">
            <div>
              <div class="cart-drawer-look__eyebrow">Complete your look</div>
              <p class="cart-drawer-look__title">${this.escape(product.title || "Product")}</p>
            </div>
            ${hasMultipleRecommendations ? `
              <div class="cart-drawer-look__nav" aria-label="Complete your look items">
                <button type="button" data-cart-drawer-look-previous aria-label="Previous complete your look item">‹</button>
                <span>${navigation.index + 1}/${navigation.total}</span>
                <button type="button" data-cart-drawer-look-next aria-label="Next complete your look item">›</button>
              </div>` : ""}
          </div>
          ${price ? `<p class="cart-drawer-look__price">$${this.escape(price)} USD</p>` : ""}
          <div class="cart-drawer-look__actions">
            <label class="visually-hidden" for="CartDrawerLookVariant">Select Color/Size</label>
            <select id="CartDrawerLookVariant" data-cart-drawer-look-variant data-fallback-image="${this.escapeAttribute(image)}" disabled>
              <option value="">Loading options…</option>
              ${options}
            </select>
            <button type="button" class="button" data-cart-drawer-look-add disabled>Add to tote</button>
          </div>
          <p class="cart-drawer-look__error" data-cart-drawer-look-error hidden></p>
        </div>
      </div>`;

    const select = host.querySelector("[data-cart-drawer-look-variant]");
    select.addEventListener("change", (event) => {
      // CartDrawerItems listens for every bubbled change and assumes it is a quantity input.
      event.stopPropagation();
      this.onVariantChange(event);
    });

    host.hidden = false;
    this.hydrateVariants(host);
  }

  onVariantChange(event) {
    const select = event.currentTarget.closest("[data-cart-drawer-look-variant]");
    if (!select) return;
    const host = select.closest("[data-cart-drawer-shop-the-look]");
    const button = host?.querySelector("[data-cart-drawer-look-add]");
    const option = select.selectedOptions[0];
    if (button) button.disabled = !select.value || option?.disabled || option?.dataset.available === "false";
    this.updateImage(host, option?.dataset.image || select.dataset.fallbackImage);
    this.showError(host, "");
  }

  async hydrateVariants(host) {
    const select = host.querySelector("[data-cart-drawer-look-variant]");
    if (!select) return;

    const options = [...select.options].filter((option) => option.value);
    await Promise.all(options.map(async (option) => {
      try {
        const variant = await this.getVariant(option.value);
        option.dataset.available = String(variant.available !== false);
        option.dataset.image = this.variantImageUrl(variant) || "";
        if (variant.available === false) {
          option.disabled = true;
          option.textContent = `${option.textContent} — Sold out`;
        }
      } catch (error) {
        option.dataset.available = "unknown";
      }
    }));

    if (!host.isConnected) return;

    const availableOptions = options.filter((option) => !option.disabled && option.dataset.available !== "false");
    const placeholder = select.options[0];
    placeholder.textContent = "Select Color/Size";

    if (availableOptions.length === 1) {
      host.classList.add("cart-drawer-look--single-variant");
      select.value = availableOptions[0].value;
      select.classList.add("cart-drawer-look__select--single");
      select.setAttribute("aria-label", availableOptions[0].textContent);
      select.disabled = true;
      const button = host.querySelector("[data-cart-drawer-look-add]");
      if (button) button.disabled = false;
      this.updateImage(host, availableOptions[0].dataset.image || select.dataset.fallbackImage);
      return;
    }

    select.disabled = false;
  }

  async onClick(event) {
    const nextButton = event.target.closest("[data-cart-drawer-look-next]");
    const previousButton = event.target.closest("[data-cart-drawer-look-previous]");
    if (nextButton || previousButton) {
      event.preventDefault();
      event.stopPropagation();
      this.changeRecommendation(nextButton ? 1 : -1);
      return;
    }

    const button = event.target.closest("[data-cart-drawer-look-add]");
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    const host = button.closest("[data-cart-drawer-shop-the-look]");
    const select = host?.querySelector("[data-cart-drawer-look-variant]");
    if (!select?.value) return;

    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    try {
      const variant = await this.getVariant(select.value);
      if (variant.available === false) {
        const option = select.selectedOptions[0];
        if (option) {
          option.disabled = true;
          option.dataset.available = "false";
          if (!option.textContent.includes("Sold out")) option.textContent += " — Sold out";
        }
        select.value = "";
        this.showError(host, "That option is sold out. Please choose another.");
        throw new Error("Selected variant is sold out");
      }

      const drawer = document.querySelector("cart-drawer");
      const sections = drawer.getSectionsToRender().map((section) => section.id);
      const formData = new FormData();
      formData.append("id", select.value);
      formData.append("quantity", "1");
      formData.append("sections", sections.join(","));
      formData.append("sections_url", window.location.pathname);

      const response = await fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/javascript",
        },
        body: formData,
      });
      const responseText = await response.text();
      let added;
      try {
        added = JSON.parse(responseText);
      } catch (error) {
        throw new Error(`Unexpected cart response (${response.status})`);
      }
      if (!response.ok || added.status) throw new Error(added.description || "Could not add product");

      drawer.renderContents(added);
      document.dispatchEvent(new CustomEvent("theme:cart-add", {
        bubbles: true,
        detail: { isEmpty: false },
      }));
    } catch (error) {
      console.error("Cart drawer Shop the Look add failed", error);
      if (host?.isConnected && !host.querySelector("[data-cart-drawer-look-error]")?.textContent) {
        this.showError(host, "This item could not be added. Please try another option.");
      }
      button.disabled = !select?.value;
    } finally {
      button.removeAttribute("aria-busy");
    }
  }

  changeRecommendation(direction) {
    const state = this.readState();
    const total = Number(document.querySelector("[data-cart-drawer-shop-the-look] .cart-drawer-look__nav span")?.textContent?.split("/")[1]) || 1;
    state.activeRecommendationIndex = (this.clampIndex(state.activeRecommendationIndex, total) + direction + total) % total;
    this.writeState(state);
    this.refresh();
  }

  updateImage(host, imageUrl) {
    const image = host?.querySelector("[data-cart-drawer-look-image]");
    if (!image || !imageUrl) return;
    image.src = imageUrl;
  }

  fetchJson(url) {
    return fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } }).then((response) => {
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return response.json();
    });
  }

  getVariant(variantId) {
    if (!this.variantCache.has(variantId)) {
      this.variantCache.set(
        variantId,
        this.fetchJson(`${window.Shopify.routes.root}variants/${variantId}.js`).catch((error) => {
          this.variantCache.delete(variantId);
          throw error;
        })
      );
    }
    return this.variantCache.get(variantId);
  }

  showError(host, message) {
    const error = host?.querySelector("[data-cart-drawer-look-error]");
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  readState() {
    try {
      return {
        activeAnchorId: null,
        activeProductIds: [],
        activeRecommendationIndex: 0,
        completedProductIds: [],
        ...JSON.parse(sessionStorage.getItem(this.storageKey) || "{}"),
      };
    } catch (error) {
      return { activeAnchorId: null, activeProductIds: [], activeRecommendationIndex: 0, completedProductIds: [] };
    }
  }

  writeState(state) {
    sessionStorage.setItem(this.storageKey, JSON.stringify(state));
  }

  variantId(value) {
    const match = String(value || "").match(/ProductVariant\/(\d+)/);
    return match ? match[1] : String(value || "");
  }

  imageUrl(product) {
    const image = product.image || product.imageUrl || product.featuredImage || "";
    return typeof image === "string" ? image : image.src || image.url || "";
  }

  variantImageUrl(variant) {
    const image = variant.featured_image || variant.image || "";
    return typeof image === "string" ? image : image.src || image.url || "";
  }

  clampIndex(value, total) {
    const index = Number(value) || 0;
    if (total < 1) return 0;
    return Math.min(Math.max(index, 0), total - 1);
  }

  unique(values) {
    return [...new Set(values.map(String))];
  }

  escape(value) {
    const element = document.createElement("div");
    element.textContent = String(value || "");
    return element.innerHTML;
  }

  escapeAttribute(value) {
    return this.escape(value).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }
}

window.cartDrawerShopTheLook = window.cartDrawerShopTheLook || new CartDrawerShopTheLook();
